import React, { memo, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { UI_VERSION } from '../version';
import { MAP_UI } from '../theme/mapViewUi';
import { auth } from '../services/auth';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  useTheme,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Devices as DevicesIcon,
  MonitorHeart as DiagnosticsIcon,
  Terminal as CommandsIcon,
  Analytics as AnalysisIcon,
  WifiTethering as PublicWifiIcon,
  Hub as BackhaulNetworkIcon,
  Settings as ConfigIcon,
  SettingsEthernet as ProjectSettingsIcon,
  Description as ReportsIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Logout as LogoutIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

const SIDEBAR_WIDTH = 240;

const Sidebar = ({ activeTab, onTabChange, onThemeToggle, isDarkMode, onLogout, userInfo, environment, mobileOpen, onMobileClose }) => {
  const theme = useTheme();
  const navAccent = theme.palette.primary.main;
  const navSelectedBg = theme.palette.action.selected;
  const navigate = useNavigate();
  const [configExpanded, setConfigExpanded] = useState(false);
  const [backhaulExpanded, setBackhaulExpanded] = useState(false);
  const [wifiNetworkExpanded, setWifiNetworkExpanded] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const projectLabel = useMemo(() => {
    const raw = userInfo?.projectId || auth.getProjectId();
    if (!raw) return null;
    const s = String(raw);
    const dash = s.indexOf('-');
    return dash > 0 ? s.slice(0, dash) : s;
  }, [userInfo]);

  // Auto-expand Device Config group when a child is active
  useEffect(() => {
    if (activeTab === 6 || activeTab === 7) {
      setConfigExpanded(true);
    }
  }, [activeTab]);

  // Auto-expand Backhaul Network when a child is active
  useEffect(() => {
    if ([0, 1, 2, 3].includes(activeTab)) {
      setBackhaulExpanded(true);
    }
  }, [activeTab]);

  // Auto-expand Wi-Fi Network when a child is active
  useEffect(() => {
    if (activeTab === 5 || activeTab === 9 || activeTab === 10) {
      setWifiNetworkExpanded(true);
    }
  }, [activeTab]);

  const overviewItem = { id: 8, label: 'Overview', icon: <DashboardIcon />, path: '/overview' };

  const allBackhaulItems = [
    { id: 0, label: 'Devices', icon: <DevicesIcon />, path: '/devices' },
    { id: 1, label: 'Diagnostics', icon: <DiagnosticsIcon />, path: '/diagnostics' },
    { id: 3, label: 'RRM', icon: <AnalysisIcon />, path: '/analysis', requiresBackhaulRead: true },
    { id: 2, label: 'Commands', icon: <CommandsIcon />, path: '/commands', requiresBackhaulWrite: true },
  ];
  const backhaulItems = allBackhaulItems.filter((item) => {
    if (item.requiresBackhaulWrite && !auth.canWriteBackhaul()) return false;
    if (item.requiresBackhaulRead && !auth.canReadBackhaul()) return false;
    return true;
  });

  const wifiNetworkItems = auth.canReadPublicWifi() ? [
    { id: 9, label: 'Devices', icon: <DevicesIcon />, path: '/public-wifi-overview' },
    { id: 10, label: 'WiFi RRM', icon: <AnalysisIcon />, path: '/public-wifi-expansion' },
    { id: 5, label: 'Configuration', icon: <ConfigIcon />, path: '/public-wifi' },
  ] : [];

  const configItems = auth.canWriteDeviceConfig() ? [
    { id: 6, label: 'Configuration', icon: <ConfigIcon />, path: '/configuration' },
    { id: 7, label: 'Default Config', icon: <ReportsIcon />, path: '/default-config' },
  ] : [];

  const projectConfigItem = auth.canWriteBackhaul()
    ? { id: 11, label: 'Settings', icon: <ProjectSettingsIcon />, path: '/settings' }
    : null;

  const isConfigGroupActive = activeTab === 6 || activeTab === 7;
  const isBackhaulGroupActive = [0, 1, 2, 3].includes(activeTab);
  const isWifiNetworkGroupActive = activeTab === 5 || activeTab === 9 || activeTab === 10;

  const handleNavClick = (item) => {
    onTabChange(item.id);
    // Navigate to the corresponding path to keep URL in sync with active tab
    // This ensures refresh preserves the current tab
    navigate(item.path);
    // Close mobile drawer after navigation
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const handleConfigGroupToggle = () => {
    setConfigExpanded(!configExpanded);
  };

  const handleBackhaulGroupToggle = () => {
    setBackhaulExpanded(!backhaulExpanded);
  };

  const handleWifiNetworkGroupToggle = () => {
    setWifiNetworkExpanded(!wifiNetworkExpanded);
  };

  const drawerContent = (
    <>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            bgcolor: navAccent,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#000',
            fontWeight: 900,
            fontSize: '1.2rem',
          }}
        >
          J
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
              NMS
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
              {projectLabel && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.2, textTransform: 'capitalize' }}
                >
                  {projectLabel}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.2 }}>
                v{UI_VERSION}
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {environment || 'Production'}
          </Typography>
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ flexGrow: 1, py: 1 }}>
        <ListItem key={overviewItem.id} disablePadding>
          <ListItemButton
            selected={activeTab === overviewItem.id}
            onClick={() => handleNavClick(overviewItem)}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: navSelectedBg,
                borderLeft: `3px solid ${navAccent}`,
                '&:hover': {
                  bgcolor: navSelectedBg,
                  opacity: 0.9,
                },
              },
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: activeTab === overviewItem.id
                  ? navAccent
                  : 'text.secondary',
                minWidth: 40,
              }}
            >
              {overviewItem.icon}
            </ListItemIcon>
            <ListItemText
              primary={overviewItem.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: activeTab === overviewItem.id ? 600 : 400,
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Backhaul Network */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleBackhaulGroupToggle}
            selected={isBackhaulGroupActive}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: navSelectedBg,
                borderLeft: `3px solid ${navAccent}`,
                '&:hover': {
                  bgcolor: navSelectedBg,
                  opacity: 0.9,
                },
              },
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isBackhaulGroupActive
                  ? navAccent
                  : 'text.secondary',
                minWidth: 40,
              }}
            >
              <BackhaulNetworkIcon />
            </ListItemIcon>
            <ListItemText
              primary="Backhaul Network"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isBackhaulGroupActive ? 600 : 400,
              }}
            />
            {backhaulExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </ListItemButton>
        </ListItem>

        <Collapse in={backhaulExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {backhaulItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={activeTab === item.id}
                  onClick={() => handleNavClick(item)}
                  sx={{
                    mx: 1,
                    ml: 4,
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: navSelectedBg,
                      borderLeft: `3px solid ${navAccent}`,
                      '&:hover': {
                        bgcolor: navSelectedBg,
                        opacity: 0.9,
                      },
                    },
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: activeTab === item.id
                        ? navAccent
                        : 'text.secondary',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: activeTab === item.id ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>

        {wifiNetworkItems.length > 0 && (
        <>
        {/* Wi-Fi Network */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleWifiNetworkGroupToggle}
            selected={isWifiNetworkGroupActive}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: navSelectedBg,
                borderLeft: `3px solid ${navAccent}`,
                '&:hover': {
                  bgcolor: navSelectedBg,
                  opacity: 0.9,
                },
              },
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isWifiNetworkGroupActive
                  ? navAccent
                  : 'text.secondary',
                minWidth: 40,
              }}
            >
              <PublicWifiIcon />
            </ListItemIcon>
            <ListItemText
              primary="Wi-Fi Network"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isWifiNetworkGroupActive ? 600 : 400,
              }}
            />
            {wifiNetworkExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </ListItemButton>
        </ListItem>

        <Collapse in={wifiNetworkExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {wifiNetworkItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={activeTab === item.id}
                  onClick={() => handleNavClick(item)}
                  sx={{
                    mx: 1,
                    ml: 4,
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: navSelectedBg,
                      borderLeft: `3px solid ${navAccent}`,
                      '&:hover': {
                        bgcolor: navSelectedBg,
                        opacity: 0.9,
                      },
                    },
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: activeTab === item.id
                        ? navAccent
                        : 'text.secondary',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: activeTab === item.id ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
        </>
        )}

        {configItems.length > 0 && (
        <>
        {/* Device Config Group */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleConfigGroupToggle}
            selected={isConfigGroupActive}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: navSelectedBg,
                borderLeft: `3px solid ${navAccent}`,
                '&:hover': {
                  bgcolor: navSelectedBg,
                  opacity: 0.9,
                },
              },
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isConfigGroupActive 
                  ? navAccent
                  : 'text.secondary',
                minWidth: 40,
              }}
            >
              <ConfigIcon />
            </ListItemIcon>
            <ListItemText
              primary="Device Config"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isConfigGroupActive ? 600 : 400,
              }}
            />
            {configExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </ListItemButton>
        </ListItem>

        {/* Device Config Children */}
        <Collapse in={configExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {configItems.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={activeTab === item.id}
                  onClick={() => handleNavClick(item)}
                  sx={{
                    mx: 1,
                    ml: 4,
                    borderRadius: 1,
                    mb: 0.5,
                    '&.Mui-selected': {
                      bgcolor: navSelectedBg,
                      borderLeft: `3px solid ${navAccent}`,
                      '&:hover': {
                        bgcolor: navSelectedBg,
                        opacity: 0.9,
                      },
                    },
                    '&:hover': {
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: activeTab === item.id 
                        ? navAccent
                        : 'text.secondary',
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: activeTab === item.id ? 600 : 400,
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
        </>
        )}

        {projectConfigItem && (
        <ListItem key={projectConfigItem.id} disablePadding>
          <ListItemButton
            selected={activeTab === projectConfigItem.id}
            onClick={() => handleNavClick(projectConfigItem)}
            sx={{
              mx: 1,
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: navSelectedBg,
                borderLeft: `3px solid ${navAccent}`,
                '&:hover': {
                  bgcolor: navSelectedBg,
                  opacity: 0.9,
                },
              },
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: activeTab === projectConfigItem.id
                  ? navAccent
                  : 'text.secondary',
                minWidth: 40,
              }}
            >
              {projectConfigItem.icon}
            </ListItemIcon>
            <ListItemText
              primary={projectConfigItem.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: activeTab === projectConfigItem.id ? 600 : 400,
              }}
            />
          </ListItemButton>
        </ListItem>
        )}
      </List>

      <Divider />

      {/* Footer Controls */}
      <Box sx={{ p: 2 }}>
        {/* User Info */}
        {userInfo && userInfo.name && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}>
              Logged in as
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              {userInfo.name}
            </Typography>
            {userInfo.userType && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem', mt: 0.25 }}>
                {userInfo.userType}
              </Typography>
            )}
          </Box>
        )}

        {/* Theme Toggle */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={onThemeToggle}
            size="small"
            sx={{
              flex: 1,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            {isDarkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
          </IconButton>
          <IconButton
            onClick={onLogout}
            size="small"
            sx={{
              flex: 1,
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
              color: MAP_UI.accentMuted,
              '&:hover': {
                bgcolor: 'action.hover',
                color: MAP_UI.accentDark,
              },
            }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            bgcolor: theme.palette.mode === 'dark' ? '#0D1117' : '#FFFFFF',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Permanent Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: SIDEBAR_WIDTH,
            boxSizing: 'border-box',
            bgcolor: theme.palette.mode === 'dark' ? '#0D1117' : '#FFFFFF',
            borderRight: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default memo(Sidebar);
export { SIDEBAR_WIDTH };

